import { MarkdownEditor } from '@/js/shared/components/markdown/MarkdownEditor';
import { MarkdownRender } from '@/js/shared/components/markdown/MarkdownRender';
import { useCoeolytics } from '@/js/shared/context/CoeolyticsHooks';
import { infoList } from '@/js/shared/utils/DataFormat';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import axios from 'axios';
import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Anchorme } from 'react-anchorme';
import C from '../../../../../shared/constants/general';
import { getImage, ImageSize } from '../../../../../shared/utils/imageFormat';
import DiscussionList from '../../../shared/components/discussion/DiscussionList';
import { FileConverter } from '../../../shared/components/file-upload';
import DropZone from '../../../shared/components/ui/DropZone';
import RedButton from '../../../shared/components/ui/RedButton';
import ResponsiveButton from '../../../shared/components/ui/ResponsiveButton';
import UserAvatar from '../../../shared/components/ui/UserAvatar';
import { AuthContext } from '../../../shared/context/AuthContext';
import { ConferenceContext } from '../../../shared/context/ConferenceContext';
import { PusherContext } from '../../../shared/context/PusherContext';
import { VideoChatContext } from '../../../shared/context/VideoChatContext';
import { cipherDecode, cipherEncode } from '../../../shared/utils/StringFormat';
import { LiveContainer } from '../../LiveContainer';
import { EntityBookingList } from '../bookings/EntityBookingList';
import { EntitySlide } from '../info/EntitySlide';
import EntityVideoHandler from '../interact/EntityVideoHandler';
import { ListVisitors } from '../intities/ListVisitors';
import { ReceiveECards } from '../intities/ReceiveECards';
import { EntityAddVisitor } from './EntityAddVisitor';

const slideTypes = require('../../../../../shared/schemas/entities/entitySlide.json');

const AddSlide = React.memo((props) => {
  const [adding, toggleAdding] = useState(props.open || false);
  const getDefault = (type) => {
    return {
      type: type,
      data: Object.entries(slideTypes.properties[type].properties).reduce(
        (obj, [key, val]) => {
          obj[key] = val.default;
          return obj;
        },
        {}
      ),
    };
  };

  return (
    <Box align="center">
      {adding ? (
        <Grid container spacing={1} justifyContent="center">
          {Object.entries(slideTypes.properties)
            .filter(([, t]) => !t.noadd)
            .map(([key, t]) => (
              <Grid item xs={6} md={2} key={t.name}>
                <Button
                  size="small"
                  color="primary"
                  startIcon={<Icon>{t.icon}</Icon>}
                  onClick={() =>
                    props.startEdit(props.index, getDefault(key), true)
                  }
                >
                  +{t.name}
                </Button>
              </Grid>
            ))}
        </Grid>
      ) : (
        <IconButton
          size="small"
          color="primary"
          onClick={() => toggleAdding(true)}
        >
          <Icon>add_circle</Icon>
        </IconButton>
      )}
    </Box>
  );
});

const EditSlideLinks = React.memo((props) => {
  const handleInputChange = (linkIndex) => (e) => {
    const newList = [...props.data.links];
    newList[linkIndex] = {
      ...newList[linkIndex],
      [e.target.name]: e.target.value,
    };
    props.callback({ links: newList });
  };
  const deleteLink = (linkIndex) => {
    const newList = [...props.data.links];
    newList.splice(linkIndex, 1);
    props.callback({ links: newList });
  };
  const addLink = () => {
    props.callback({
      links: [
        ...props.data.links,
        slideTypes.properties.links.properties.links.default[0],
      ],
    });
  };
  return (
    <Box>
      {props.data.links.map((link, index) => (
        <Box display="flex" key={index} alignItems="center">
          <Box p={1} flexGrow={1}>
            <TextField
              autoComplete="off"
              variant="outlined"
              fullWidth
              name="text"
              label={
                slideTypes.properties.links.properties.links.properties.text
                  .description
              }
              value={link.text}
              onChange={handleInputChange(index)}
            />
          </Box>
          <Box p={1} flexGrow={1}>
            <TextField
              autoComplete="off"
              variant="outlined"
              fullWidth
              name="url"
              label={
                slideTypes.properties.links.properties.links.properties.url
                  .description
              }
              value={link.url}
              onChange={handleInputChange(index)}
            />
          </Box>
          {props.data.links.length > 1 && (
            <Box p={1}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => deleteLink(index)}
              >
                <Icon>delete</Icon>
              </IconButton>
            </Box>
          )}
        </Box>
      ))}
      <Box align="center">
        <Button
          startIcon={<Icon>add_circle</Icon>}
          size="small"
          color="primary"
          onClick={addLink}
        >
          another link
        </Button>
      </Box>
    </Box>
  );
});

const EditSlideText = React.memo((props) => {
  const handleInputChange = (e) => {
    const newData = { ...props.data, [e.target.name]: e.target.value };
    props.callback(newData);
  };
  return (
    <TextField
      autoComplete="off"
      variant="outlined"
      fullWidth
      multiline
      rows={4}
      name="text"
      label={slideTypes.properties.text.properties.description}
      value={props.data.text}
      onChange={handleInputChange}
    />
  );
});

const editSlideIframeStyles = makeStyles(() => ({
  guideImg: {
    width: 'auto',
    borderRadius: 5,
    marginTop: 5,
    opacity: 0.6,
    maxWidth: '100%',
  },
}));

const EditSlideIframe = (props) => {
  const classes = editSlideIframeStyles();
  const handleInputChange = (e) => {
    const newData = { ...props.data, [e.target.name]: e.target.value };
    props.callback(newData);
  };
  return (
    <Box>
      <TextField
        autoComplete="off"
        placeholder="https://www.example.com"
        variant="outlined"
        fullWidth
        name="src"
        label={slideTypes.properties.iframe.properties.description}
        value={props.data.src}
        onChange={handleInputChange}
      />
      <img src="/images/entity_embed_guide.png" className={classes.guideImg} />
    </Box>
  );
};

const AddSlidePPT = React.memo(({ callback, entityId }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Box p={1}>
        <FileConverter
          entityId={entityId}
          btnLabel="Upload Powerpoint"
          convertTo="jpg"
          btnSize="small"
          btnVariant="contained"
          acceptedFiletypes={[
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.ms-powerpoint',
          ]}
          onSuccess={(files) =>
            callback(
              files.map((file) => ({
                type: 'image',
                data: { src: file.generated },
              }))
            )
          }
        />
      </Box>
    </Box>
  );
});

const EditSlideImage = React.memo(({ data, callback, entityId }) => {
  const isEdit = Object.keys(data).length === 1;
  const [uploading, setUploading] = useState(false);
  const [uploadText, setUploadText] = useState('');
  const uploadStart = () => setUploading(true);
  const uploadHandler = (fileInfo) => {
    callback(
      fileInfo.length !== undefined
        ? fileInfo.map((file) => ({
            type: 'image',
            data: { src: file.generated },
          }))
        : { src: fileInfo.generated }
    );
    setUploading(false);
  };

  const queueStatus = (status) =>
    setUploadText(
      `${status.finished.length}/${
        status.queue.length + status.finished.length
      }`
    );

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Box>
        {uploading && <CircularProgress disableShrink />}
        {uploadText !== '' && <Box>{uploadText}</Box>}
        {data.src && (
          <img
            style={{ width: '120px' }}
            src={getImage(data.src, ImageSize.Small)}
          />
        )}
      </Box>
      <Box p={1}>
        <DropZone
          acceptedfiletypes="image/jpeg, image/png, image/webp"
          uploadStart={uploadStart}
          uploadHandler={uploadHandler}
          queueStatus={!isEdit ? queueStatus : isEdit}
          multiple={!isEdit}
          buttonText={`Choose ${isEdit ? 'image' : 'images'}`}
          uploadurl={`/api/visitor/entities/${entityId}/signmediaupload`}
        />
      </Box>
    </Box>
  );
});

const ENTITY_SLIDE_TYPES = {
  links: EditSlideLinks,
  text: EditSlideText,
  note: EditSlideText,
  image: EditSlideImage,
  images: EditSlideImage,
  powerpoints: AddSlidePPT,
  iframe: EditSlideIframe,
};
const editSlideStyles = makeStyles(() => ({
  picture: {
    width: 60,
    height: 60,
  },
}));
const DEFAULT_STATE = {
  open: false,
  slide: null,
  onSave: null,
};

const EditSlide = React.memo(
  React.forwardRef((props, ref) => {
    const theme = useTheme();
    const classes = editSlideStyles();
    const [dialogState, setDialog] = useState(DEFAULT_STATE);
    const cancelHandler = (event, reason) => {
      if (reason === 'backdropClick' || reason === 'backdropClick') {
        return false;
      }
      setDialog(DEFAULT_STATE);
    };

    const save = () => {
      if (!slideTypes.properties[dialogState.slide.type].nosave) {
        dialogState.onSave(dialogState.slide);
      }
      cancelHandler();
    };
    const updateEntityData = (data) => {
      if (slideTypes.properties[dialogState.slide.type].nosave) {
        // Dont create a slide (used for adding multiple images)
        dialogState.onSave(data);
        cancelHandler();
      } else {
        // Update the slide data
        const newData = { ...dialogState.slide.data, ...data };
        setDialog({
          ...dialogState,
          slide: { ...dialogState.slide, data: newData },
        });
      }
    };

    useImperativeHandle(ref, () => ({
      open: (slideData, onSave) => {
        setDialog({ slide: { ...slideData }, open: true, onSave: onSave });
      },
    }));
    const DataTypeEditor =
      dialogState.slide && ENTITY_SLIDE_TYPES[dialogState.slide.type];

    return (
      <Dialog
        // disableBackdropClick
        // disableEscapeKeyDown
        open={dialogState.open}
        onClose={cancelHandler}
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.shadedown_10,
          },
        }}
        fullWidth={true}
        maxWidth="lg"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {dialogState.slide && (
          <>
            <DialogTitle id="alert-dialog-title">
              {`${dialogState.slide.id ? 'Edit ' : 'New '} ${
                slideTypes.properties[dialogState.slide.type].name
              }`}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                {slideTypes.properties[dialogState.slide.type].description}
              </Typography>
            </DialogContent>
            <DialogContent>
              <Box>
                <DataTypeEditor
                  data={dialogState.slide.data}
                  callback={updateEntityData}
                  entityId={props.entityId}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <Box display="flex" className={classes.buttonList}>
                <Box flexGrow={1} align="right">
                  <Button onClick={cancelHandler}>Cancel</Button>
                  <Button color="primary" onClick={save}>
                    Done
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
    );
  }),
  () => true
);

const profileStyles = makeStyles((theme) => ({
  picture: {
    width: 60,
    height: 60,
  },
  name: {
    fontSize: '0.8rem',
  },
  title: {
    fontSize: '0.7rem',
    // textTransform: 'uppercase',
    opacity: 0.7,
  },
  profileName: {
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // field: {
  //   backgroundColor: theme.palette.background.shadedown_20,
  //   // color: '#ffffff',
  // },
  bg: {
    backgroundColor: theme.palette.background.shadedown_20,
    borderRadius: 5,
    height: '100%',
    overflow: 'hidden',
  },
}));

const PublicProfiles = React.memo((props) => {
  const classes = profileStyles();
  const { openNewThreadPopup, openUserPopup } = useContext(ConferenceContext); // Hämta Context
  const { handleSendInvitation } = useContext(VideoChatContext);

  if (!props.visitors) {
    return null;
  }
  const sortedVisitors =
    props.visitors.sort(
      (a, b) => a.entityVisitors.order > b.entityVisitors.order
    ) || [];

  return sortedVisitors.map((v) => (
    <Grid
      item
      xs={12}
      sm={6}
      md={12}
      lg={sortedVisitors.length > 3 ? 6 : 12}
      key={v.id}
      style={v.entityVisitors.hidden ? { opacity: 0.5 } : {}}
    >
      <Box p={1} className={classes.bg}>
        <Box display="flex" alignItems="flex-start">
          <ButtonBase onClick={() => openUserPopup(v)}>
            <UserAvatar user={v} />
          </ButtonBase>
          <Box flexGrow={1} pl={1}>
            <Typography variant="body1" className={classes.name}>
              <ButtonBase
                onClick={() => openUserPopup(v)}
                className={classes.profileName}
              >
                {v.full_name}
              </ButtonBase>
            </Typography>
            <Typography variant="body2" className={classes.title}>
              {infoList([v.title, v.location_city])}
            </Typography>
          </Box>
        </Box>
        <Box pt={1} display="flex" alignItems="flex-start">
          <Box pr={1}>
            <Button
              startIcon={<Icon>forum</Icon>}
              color="primary"
              size="small"
              onClick={() => openNewThreadPopup([v], 'center')}
            >
              chat
            </Button>
          </Box>
          {/* <Box>
            <Button
              startIcon={<Icon>videocam</Icon>}
              color="primary"
              size="small"
              onClick={() => handleSendInvitation(v)}
            >
              video
            </Button>
          </Box> */}
          {props.editing && (
            <Box>
              <IconButton
                color="primary"
                size="small"
                onClick={() => props.handleDelete(v)}
              >
                <Icon>delete</Icon>
              </IconButton>
            </Box>
          )}
        </Box>
        <Typography variant="body2">
          {v.info_description &&
            (v.info_description.length > 170 ? (
              <>
                {v.info_description.substring(0, 170)}...
                <ButtonBase
                  onClick={() => openUserPopup(v)}
                  className={classes.profileName}
                >
                  Read more
                </ButtonBase>
              </>
            ) : (
              v.info_description
            ))}
        </Typography>
      </Box>
    </Grid>
  ));
});

const drawSlidesStyles = makeStyles((theme) => ({
  slideHolder: {
    position: 'relative',
  },
  slideEdit: {
    position: 'absolute',
    top: 5,
    right: 15,
    padding: 0,
    borderRadius: 5,
    backgroundColor: theme.palette.background.shadedown_20,
  },
  slideDelete: {
    position: 'absolute',
    top: 5,
    right: 55,
    padding: 0,
    borderRadius: 5,
    backgroundColor: theme.palette.background.shadedown_20,
  },
  slideUpDown: {
    position: 'absolute',
    top: 5,
    left: 15,
    padding: 0,
    borderRadius: 5,
    backgroundColor: theme.palette.background.shadedown_20,
  },
}));

const DrawSlides = React.memo(
  (props) => {
    const classes = drawSlidesStyles();

    return props.slides.map((as, index) => (
      <React.Fragment key={index}>
        <Box className={classes.slideHolder}>
          <EntitySlide data={{ type: as.type, data: as.data }} />
          {props.editing && (
            <>
              <Box className={classes.slideEdit}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => props.startEdit(index, as)}
                >
                  <Icon>edit</Icon>
                </IconButton>
              </Box>
              <Box className={classes.slideDelete}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => props.deleteSlide(index, as)}
                >
                  <Icon>delete</Icon>
                </IconButton>
              </Box>
              <Box
                className={classes.slideUpDown}
                display="flex"
                flexDirection="column"
              >
                {index > 0 && (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => props.moveSlide(index, -1)}
                  >
                    <Icon>expand_less</Icon>
                  </IconButton>
                )}
                {index < props.slides.length - 1 && (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => props.moveSlide(index, 1)}
                  >
                    <Icon>expand_more</Icon>
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Box>
        {props.editing && (
          <AddSlide startEdit={props.startEdit} index={index + 1} />
        )}
      </React.Fragment>
    ));
  },
  (nextProps, prevProps) =>
    nextProps.editing === prevProps.editing &&
    nextProps.nrSlides === prevProps.nrSlides &&
    nextProps.forceUpdate === prevProps.forceUpdate
);

const useStyles = makeStyles((theme) => ({
  picture: {
    width: 'auto',
    maxWidth: '100%',
    maxHeight: 150,
  },
  title: {},
  description: {
    // whiteSpace: 'pre-wrap',
    overflow: 'hidden',
  },
  fullscreenInfo: {
    minHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  fullscreenLive: {
    // height: 'calc(100vh - 48px)',
    // minHeight: '-webkit-fill-available',
    // maxHeight: '-webkit-fill-available',
    // overflow: 'hidden',
  },
  entityHolder: {
    backgroundColor: theme.palette.background.default,
    position: 'relative',
    paddingBottom: 40,
  },

  imageLogo: {
    width: 25,
    height: 25,
    borderRadius: 5,
    backgroundColor: '#000000',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  editButton: {
    backgroundColor: theme.palette.background.shadedown_20,
    padding: 5,
  },
  saveButton: {
    backgroundColor: theme.palette.background.shadedown_20,
    padding: 5,
    position: 'fixed',
    top: 60,
    left: '50%',
    width: 160,
    marginLeft: -80,
    zIndex: 10,
    borderStyle: 'solid',
    borderWidth: 2,
    borderColor: theme.palette.primary.main,
  },
  videoHolder: {
    maxWidth: 1042,
    margin: '0 auto',
  },
  startLiveBg: {
    backgroundColor: theme.palette.background.shadedown_10,
    borderRadius: 5,
    height: '100%',
  },
  editLiveSettings: {
    backgroundColor: theme.palette.background.default,
  },
  logoHolder: {
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      textAlign: 'right',
    },
  },
  backgroundFill: {
    position: 'relative',
    paddingBottom: '40%',
    height: 0,
    backgroundColor: '#000000',
    borderRadius: 10,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
  },
  backgroundEditor: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.background.default,
    padding: 5,
  },
  backgroundMinPadding: {
    height: 60,
  },

  logoOverBackground: {
    position: 'relative',
    marginLeft: 50,
    marginTop: -130,
    width: 160,
    height: 160,
    borderRadius: 10,
    backgroundColor: '#333',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    [theme.breakpoints.down('xs')]: {
      marginTop: -90,
      marginLeft: 20,
      width: 120,
      height: 120,
    },
  },
  logoEditor: {
    position: 'relative',
    backgroundColor: theme.palette.background.default,
    padding: 5,
  },
  pictureEditPen: {
    backgroundColor: theme.palette.background.default,
    position: 'absolute',
    bottom: 8,
    right: 8,
  },
  colorPicker: {
    margin: '4px 4px 4px 0',
    position: 'relative',
    // opacity: 0,
    width: 55,
    height: 30,
    borderColor: theme.palette.text.primary,
    borderWidth: 2,
    borderStyle: 'solid',
    '-webkit-appearance': 'none',
    '&::-webkit-color-swatch-wrapper': {
      padding: 0,
    },
    '&::-webkit-color-swatch': {
      border: 0,
    },
  },
  colorPickerField: {
    width: 80,
  },
  backIcon: {
    paddingLeft: 5,
  },
  liveVideoHolder: {
    position: 'relative',
    width: '100%',
  },
  extraContainer: {
    borderRadius: 5,
    backgroundColor: theme.palette.background.shadedown_10,
  },
  meetingliveContainer: {
    borderRadius: 0,
    backgroundColor: theme.palette.background.shadedown_10,
  },
  advancedSetting: {
    '&>div': {
      borderRadius: 8,
      backgroundColor: theme.palette.background.shadedown_10,
      borderColor: theme.palette.background.default,
      borderStyle: 'solid',
      borderSize: 5,
    },
  },
  protected: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 5,
    fontSize: '1.4rem',
  },
}));

const DESCRIPTION_MAX_HEIGHT = 200;

const ForumTopics = ({ entity, handleExtraDataChange }) => {
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    console.log('reload');
    axios
      .get(`/api/visitor/conferences/${window.CONFERENCE_ID}/discussiontopics`)
      .then((response) => {
        setTopics(response.data);
      });
  }, []);
  return topics.length > 0 ? (
    <TextField
      value={entity.extra_data.discussion.slug || ''}
      onChange={handleExtraDataChange('discussion')}
      inputProps={{ 'data-forname': 'slug' }}
      name="slug"
      label="Group to Forum topic"
      variant="outlined"
      select
      SelectProps={{
        native: true,
      }}
      fullWidth={true}
      InputLabelProps={{ shrink: true }}
    >
      <option value="">None</option>
      {topics.map((topic) => (
        <option value={topic.slug} key={topic.slug}>
          {topic.title}
        </option>
      ))}
    </TextField>
  ) : (
    'No forum topics.. (Create a forum topic first!)'
  );
};

const EntityViewFullscreen = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const editRef = useRef();
  const addHostRef = useRef();
  const descriptionRef = useRef(); // Used to check height
  const { authState } = useContext(AuthContext); // Hämta Context
  const { pusher, ROOM_TYPE, actions: pusherActions } = useContext(
    PusherContext
  ); // Hämta Context
  const { openNewThreadPopup } = useContext(ConferenceContext); // Hämta Context
  const coeoLytics = useCoeolytics();

  const [viewLive, setViewLive] = useState(false);
  const [showLiveButtons, toggleLiveButtons] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingLogo, setEditingLogo] = useState(false);
  const [editingBg, setEditingBg] = useState(false);
  const [editAdvanced, setAdvanced] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    picture: false,
    bg_picture: false,
  });
  const [entity, setEntity] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [entityNeighbours, setEntityNeighbours] = useState({
    next: null,
    prev: null,
  });
  const [tooLongDescription, setTooLongDescription] = useState(false);
  const [showFullDescription, toggleFullDescription] = useState(false);
  const [bookingUpdates, setBookingUpdates] = useState(0);

  const [slideData, setSlides] = useState({ updated: 0, slides: [] });

  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const cancelEdit = () => {
    refresh();
    setEditing(false);
  };

  const saveEdit = () => {
    axios
      .put(`/api/visitor/entities/${props.entityId}/update`, {
        entity: entity,
        slides: slideData.slides,
      })
      .then((response) => {
        const isPublicProfile = response.data.visitors.find(
          (v) => v.id === authState.user.id
        );
        setEntity({
          ...response.data,
          isPublicProfile: isPublicProfile,
          extra_data: response.data.extra_data || {},
          hasAdminAccess:
            authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR ||
            (isPublicProfile && response.data.entityList.type === 'normal'),
        });
        setEditing(false);
      });
  };

  const addHost = (taggedVisitorId, passData) => {
    return axios.post(`/api/visitor/entities/${props.entityId}/publicvisitor`, {
      visitorId: taggedVisitorId,
      ...passData,
    });
  };
  const startEdit = (index, slide, isNew) => {
    editRef.current.open(slide, (data) => {
      const newSlides = [...slideData.slides];
      if (isNew) {
        // Insert
        newSlides.splice(index, 0, ...(Array.isArray(data) ? data : [data]));
      } else {
        // Update
        newSlides[index] = { ...data, update: Math.random() }; // Add update just for rendering
      }
      setSlides({ update: Math.random(), slides: newSlides });
    });
  };

  const deleteSlide = (index) => {
    const newSlides = [...slideData.slides];
    newSlides.splice(index, 1);
    setSlides({ update: Math.random(), slides: newSlides });
  };

  const moveSlide = (index, dir) => {
    const newSlides = [...slideData.slides];
    let cutOut = newSlides.splice(index, 1)[0]; // cut the element at index 'from'
    newSlides.splice(index + dir, 0, cutOut);
    setSlides({ update: Math.random(), slides: newSlides });
  };

  const gotoEntity = (entityId) => {
    props.history.replace(`/live/item/${entityId}`);
  };

  const handleInputChange = (e) => {
    setEntity({ ...entity, [e.target.name]: e.target.value });
  };

  const handleExtraDataChange = (data) => (e) => {
    try {
      const newExtra = { ...entity.extra_data };
      newExtra[data][e.target.name] = e.target.value;
      setEntity({ ...entity, extra_data: newExtra });
    } catch (e) {
      console.log('error in fields', e);
    }
  };
  const toggleExtraDataCheckbox = (data) => (e) => {
    try {
      const newExtra = { ...entity.extra_data };
      newExtra[data][e.target.name] = e.target.checked;
      setEntity({ ...entity, extra_data: newExtra });
    } catch (e) {
      console.log('error in fields', e);
    }
  };
  const toggleExtraData = (extraType, val) => {
    const newExtra = { ...entity.extra_data };
    if (val) {
      newExtra[extraType] = val;
    } else {
      delete newExtra[extraType];
    }
    setEntity({ ...entity, extra_data: newExtra });
  };

  const uploadImageStart = (imagename) => () => {
    setUploadStatus((old) => ({ ...old, [imagename]: true }));
  };

  const uploadImageHandler = (imagename) => (fileInfo) => {
    setEntity({ ...entity, [imagename]: fileInfo.generated });
    setUploadStatus((old) => ({ ...old, [imagename]: false }));
  };

  const updateLiveSettings = (livestate) => {
    axios
      .put(`/api/visitor/entities/${props.entityId}/updatelivestate`, {
        livestate,
      })
      .then((response) => {
        // Pusher will send updated info
        setEntity({ ...entity, livestate: response.data.livestate });
        setViewLive(response.data.livestate !== C.ENTITY.LIVE_STATE.OFF);
        toggleLiveButtons(false);
      });
  };

  const deleteProfile = (v) => {
    if (
      window.confirm(
        authState.user.id === v.id
          ? `Really remove yourself?`
          : `Really remove ${v.full_name}?`
      )
    ) {
      axios
        .put(`/api/visitor/entities/${props.entityId}/deletepublicvisitor`, {
          visitorId: v.id,
        })
        .then(() => {
          const visitorList = entity.visitors.filter(
            (host) => host.id !== v.id
          );
          setEntity({ ...entity, visitors: visitorList });
          if (authState.user.id === v.id) {
            setEditing(false);
          }
        });
    }
  };
  const profileAdded = (newHost) => {
    setEntity({ ...entity, visitors: [...entity.visitors, newHost] });
  };

  const refresh = () => {
    setError('');
    setPassword('');
    axios
      .get(`/api/visitor/entities/${props.entityId}`)
      .then((response) => {
        const isPublicProfile = response.data.visitors.find(
          (v) => v.id === authState.user.id
        );

        const entity = {
          ...response.data,
          extra_data: response.data.extra_data || {},
          isPublicProfile: isPublicProfile,
          hasAdminAccess:
            authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR ||
            (isPublicProfile && response.data.entityList.type === 'normal'),
        };

        setEntity(entity);

        setSlides({
          update: Math.random(),
          slides: response.data.entitySlides.map((es) => ({
            ...es.data,
            id: es.id,
          })),
        });

        // Always start without live
        setViewLive(false);

        if (!entity.entityList) {
          return;
        }

        const {
          entityList: { entities },
        } = entity;

        const entityIndex = entities.findIndex((el) => el.id === entity.id);

        const prevEntity =
          entities[(entities.length + entityIndex - 1) % entities.length];
        const nextEntity = entities[(entityIndex + 1) % entities.length];

        setEntityNeighbours({
          prev: prevEntity,
          next: nextEntity,
        });
      })
      .catch(() => {
        setError('Could not load this page');
      });
  };

  useEffect(() => {
    refresh();
    toggleLiveButtons(false); // Reset for changing entity
    if (props.history.action) {
      //Add analytics page view if via history
      coeoLytics.page();
    }
  }, [props.entityId]);

  useEffect(() => {
    if (pusher.current) {
      pusherActions.subscribe(
        ROOM_TYPE.ENTITY_COUNT,
        `${window.CONFERENCE_ID}_${props.entityId}_${authState.user.id}`
      );

      const entityChannel = pusherActions.subscribe(
        ROOM_TYPE.ENTITY,
        props.entityId
      );
      entityChannel.bind('live-state', (data) => {
        setEntity((currentEntity) => ({
          ...currentEntity,
          livestate: data.livestate,
        }));

        if (data.livestate === C.ENTITY.LIVE_STATE.OFF) {
          setViewLive(false);
        }
      });
      entityChannel.bind('booking-update', () => {
        setBookingUpdates((old) => old + 1);
      });
    }
    return () => {
      if (pusher.current) {
        pusherActions.unSubscribe(
          ROOM_TYPE.ENTITY_COUNT,
          `${window.CONFERENCE_ID}_${props.entityId}_${authState.user.id}`
        );
        pusherActions.unSubscribe(ROOM_TYPE.ENTITY, props.entityId);
      }
    };
  }, [!!pusher.current, props.entityId]);

  useEffect(() => {
    if (entity && !editing && descriptionRef.current) {
      setTooLongDescription(
        descriptionRef.current.clientHeight <
          descriptionRef.current.scrollHeight ||
          entity.description.includes('![')
      );
    }
  }, [entity && entity.description, editing]);

  // Some profiles can be public (as in admin) but hidden
  const listedPublicProfiles =
    (entity?.hasAdminAccess
      ? entity?.visitors.length
      : entity?.visitors.filter((v) => v.entityVisitors.hidden === false)
          .length) || 0;
  return (
    <LiveContainer>
      {error ? (
        <Box textAlign="center" pt={3}>
          <Typography variant="h4">404</Typography>
          <Typography variant="h6">
            Something went wrong, could not find this page.
          </Typography>
          <br />
          <br />
          <Button
            color="primary"
            href="/live"
            startIcon={<Icon>home</Icon>}
            variant="contained"
          >
            Back to start
          </Button>
        </Box>
      ) : (
        entity && (
          <Box
            display="flex"
            flexGrow={1}
            flexDirection="column"
            className={
              viewLive ? classes.fullscreenLive : classes.fullscreenInfo
            }
          >
            <Box display="flex" pb={1}>
              <Box>
                {entityNeighbours.prev && (
                  <Box display="flex" alignItems="center">
                    <IconButton
                      size="small"
                      onClick={() => gotoEntity(entityNeighbours.prev.id)}
                      color="inherit"
                      aria-label="Previous"
                    >
                      <Icon className={classes.backIcon}>arrow_back_ios</Icon>
                    </IconButton>
                    <IconButton
                      component="button"
                      onClick={() => gotoEntity(entityNeighbours.prev.id)}
                      className={classes.imageLogo}
                      style={{
                        backgroundImage: `url(${getImage(
                          entityNeighbours.prev.picture ||
                            entityNeighbours.prev.bg_picture,
                          ImageSize.Icon
                        )})`,
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box
                flexGrow={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <React.Fragment>
                  {entity.livestate !== C.ENTITY.LIVE_STATE.OFF && (
                    <React.Fragment>
                      <Box pl={1} pr={1}>
                        {viewLive && entity.hasAdminAccess && (
                          <ResponsiveButton
                            small={smallScreen}
                            onClick={() =>
                              window.confirm('Really close the video chat') &&
                              updateLiveSettings(C.ENTITY.LIVE_STATE.OFF)
                            }
                            icon="videocam_off"
                          >
                            end live
                          </ResponsiveButton>
                        )}
                      </Box>
                      <Box pl={1} pr={1}>
                        {viewLive && (
                          <ResponsiveButton
                            small={false}
                            onClick={() => setViewLive(false)}
                            icon="cancel"
                          >
                            Leave live meeting
                          </ResponsiveButton>
                        )}
                      </Box>
                    </React.Fragment>
                  )}
                  <Box pl={0} pr={1}>
                    {/*!viewLive && !entity.hasAdminAccess && (
                    <Box pl={1} pr={1}>
                      <Typography variant="h6">
                        {entity.title.substring(0, 30)}
                      </Typography>
                    </Box>
                  )*/}
                  </Box>
                </React.Fragment>
              </Box>

              <Box>
                {entityNeighbours.next && (
                  <Box display="flex" alignItems="center">
                    <IconButton
                      component="button"
                      onClick={() => gotoEntity(entityNeighbours.next.id)}
                      className={classes.imageLogo}
                      style={{
                        backgroundImage: `url(${getImage(
                          entityNeighbours.next.picture ||
                            entityNeighbours.next.bg_picture,
                          ImageSize.Icon
                        )})`,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => gotoEntity(entityNeighbours.next.id)}
                      color="inherit"
                      aria-label="Next"
                    >
                      <Icon>arrow_forward_ios</Icon>
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
            {entity.extra_data.pass &&
            !entity.hasAdminAccess &&
            entity.extra_data.pass.word !== '' &&
            entity.extra_data.pass.word !== password ? (
              <Box textAlign="center" pt={4}>
                <h4>{entity.extra_data.pass.headline}</h4>
                <TextField
                  variant="outlined"
                  onChange={(e) => setPassword(cipherEncode(e.target.value))}
                  label="Password"
                  name="setpassword"
                  value={cipherDecode(password)}
                />
              </Box>
            ) : (
              <>
                {viewLive ? (
                  <Box
                    flexGrow={1}
                    align="center"
                    className={classes.liveVideoHolder}
                    display="flex"
                  >
                    <EntityVideoHandler
                      entityId={entity.id}
                      hosts={entity.visitors.map((v) => v.id)}
                      isHost={entity.hasAdminAccess}
                      useQueue={entity.livestate === C.ENTITY.LIVE_STATE.QUEUE}
                      entity={entity}
                      isBreakout={entity.entityList.type === 'breakout'}
                      viewType="gallery"
                      bookingUpdates={bookingUpdates}
                    />
                  </Box>
                ) : (
                  <Box flexGrow={1} className={classes.entityHolder}>
                    {entity.hasAdminAccess &&
                      showLiveButtons &&
                      entity.livestate === C.ENTITY.LIVE_STATE.OFF && (
                        <Box
                          align="center"
                          pt={2}
                          pb={2}
                          className={classes.editLiveSettings}
                        >
                          <Typography variant="body2">
                            You can start two types of live rooms here
                          </Typography>

                          <Grid container spacing={1} justifyContent="center">
                            <Grid item xs={12} md={3}>
                              <Box p={1} className={classes.startLiveBg}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    updateLiveSettings(C.ENTITY.LIVE_STATE.OPEN)
                                  }
                                  startIcon={<Icon>videocam</Icon>}
                                >
                                  open room
                                </Button>
                                <br />
                                <br />
                                <Typography variant="body2">
                                  Start a room that anyone can join.
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Box p={1} className={classes.startLiveBg}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    updateLiveSettings(
                                      C.ENTITY.LIVE_STATE.QUEUE
                                    )
                                  }
                                  startIcon={<Icon>videocam</Icon>}
                                >
                                  room with queue
                                </Button>
                                <br />
                                <br />
                                <Typography variant="body2">
                                  Start a room where visitors will begin in a
                                  waiting room. You will be able to control who
                                  can join the room.
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                    {entity.hasAdminAccess && !editing && (
                      <Box
                        className={classes.editButton}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {entity.livestate === C.ENTITY.LIVE_STATE.OFF && (
                          <Box>
                            <Button
                              color="primary"
                              size="small"
                              onClick={() =>
                                toggleLiveButtons(!showLiveButtons)
                              }
                              startIcon={<Icon>videocam</Icon>}
                            >
                              {showLiveButtons ? 'cancel live' : 'Start Live'}
                            </Button>
                          </Box>
                        )}
                        <Box>
                          {entity.livestate !== C.ENTITY.LIVE_STATE.OFF &&
                            entity.hasAdminAccess && (
                              <ResponsiveButton
                                small={false}
                                onClick={() =>
                                  window.confirm(
                                    'Really close the video chat'
                                  ) &&
                                  updateLiveSettings(C.ENTITY.LIVE_STATE.OFF)
                                }
                                icon="videocam_off"
                              >
                                end live
                              </ResponsiveButton>
                            )}
                        </Box>
                        <Box>
                          <Button
                            color="primary"
                            size="small"
                            onClick={() => setEditing(!editing)}
                            startIcon={<Icon>settings</Icon>}
                          >
                            edit page
                          </Button>
                        </Box>
                        {slideData.slides.length > 0 && (
                          <Box>
                            <Button
                              color="primary"
                              size="small"
                              href={`/live/item/${props.entityId}/slideshow`}
                              target="_blank"
                              startIcon={<Icon>view_carousel</Icon>}
                            >
                              open slideshow
                            </Button>
                          </Box>
                        )}
                        {!smallScreen && (
                          <Box>
                            <Button
                              size="small"
                              href="https://www.coeo.events/guides/expo"
                              target="_blank"
                              startIcon={<Icon>info</Icon>}
                            >
                              Help guide
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                    {entity.hasAdminAccess &&
                      entity.extra_data.pass &&
                      !editing && (
                        <Box className={classes.protected}>
                          Password protected!
                        </Box>
                      )}

                    <Box p={1} pt={0}>
                      <Container maxWidth="lg">
                        <Grid
                          container
                          alignItems="flex-start"
                          justifyContent="center"
                        >
                          <Grid
                            item
                            xs={12}
                            md={
                              listedPublicProfiles === 0 && !editing
                                ? 12
                                : listedPublicProfiles > 3
                                ? 8
                                : 9
                            } // sum up to 12
                            lg={
                              listedPublicProfiles === 0 && !editing
                                ? 10
                                : listedPublicProfiles > 3
                                ? 6
                                : 7
                            } // sum up to 10
                          >
                            <Box pb={1}>
                              <Box
                                className={classes.backgroundFill}
                                style={{
                                  ...(entity.bg_picture
                                    ? {
                                        backgroundImage: `url(${getImage(
                                          entity.bg_picture,
                                          { width: 1000 }
                                        )})`,
                                      }
                                    : entity.picture && !editing
                                    ? {
                                        paddingBottom: 140,
                                      }
                                    : null),
                                  ...(entity.bg_color
                                    ? {
                                        backgroundColor: entity.bg_color,
                                      }
                                    : null),
                                }}
                              >
                                {editing &&
                                  (editingBg ? (
                                    <Box className={classes.backgroundEditor}>
                                      <Box>
                                        {uploadStatus.bg_picture ? (
                                          <CircularProgress />
                                        ) : (
                                          <DropZone
                                            acceptedfiletypes="image/jpeg, image/png, image/webp"
                                            uploadStart={uploadImageStart(
                                              'bg_picture'
                                            )}
                                            uploadHandler={uploadImageHandler(
                                              'bg_picture'
                                            )}
                                            buttonText={`bg Image 1000x400`}
                                            uploadurl={`/api/visitor/entities/${entity.id}/signmediaupload`}
                                          />
                                        )}
                                        {entity.bg_picture && (
                                          <Button
                                            startIcon={<Icon>delete</Icon>}
                                            onClick={() =>
                                              setEntity((old) => ({
                                                ...old,
                                                bg_picture: null,
                                              }))
                                            }
                                            size="small"
                                          >
                                            Remove bg image
                                          </Button>
                                        )}
                                      </Box>

                                      <Box>
                                        <input
                                          autoComplete="off"
                                          className={classes.colorPicker}
                                          name="bg_color"
                                          type="color"
                                          style={{
                                            backgroundColor: entity.bg_color,
                                          }}
                                          value={entity.bg_color}
                                          onChange={handleInputChange}
                                        />
                                        <TextField
                                          className={classes.colorPickerField}
                                          onChange={handleInputChange}
                                          name="bg_color"
                                          value={entity.bg_color}
                                        />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <Box className={classes.pictureEditPen}>
                                      <IconButton
                                        onClick={() => setEditingBg(!editingBg)}
                                        size="small"
                                      >
                                        <Icon>edit</Icon>
                                      </IconButton>
                                    </Box>
                                  ))}
                              </Box>
                              {(editing || entity.picture) && (
                                <Box
                                  className={classes.logoOverBackground}
                                  p={1}
                                  display="flex"
                                  alignItems="center"
                                  style={{
                                    backgroundImage: `url(${getImage(
                                      entity.picture,
                                      ImageSize.Medium
                                    )})`,
                                  }}
                                >
                                  {editing &&
                                    (editingLogo ? (
                                      <Box className={classes.logoEditor}>
                                        {uploadStatus.picture ? (
                                          <CircularProgress />
                                        ) : (
                                          <DropZone
                                            acceptedfiletypes="image/jpeg, image/png, image/webp"
                                            uploadStart={uploadImageStart(
                                              'picture'
                                            )}
                                            uploadHandler={uploadImageHandler(
                                              'picture'
                                            )}
                                            buttonText={`logo 400x400`}
                                            uploadurl={`/api/visitor/entities/${entity.id}/signmediaupload`}
                                          />
                                        )}
                                        {entity.picture && (
                                          <Button
                                            startIcon={<Icon>delete</Icon>}
                                            onClick={() =>
                                              setEntity((old) => ({
                                                ...old,
                                                picture: null,
                                              }))
                                            }
                                            size="small"
                                          >
                                            Remove logo
                                          </Button>
                                        )}
                                      </Box>
                                    ) : (
                                      <Box className={classes.pictureEditPen}>
                                        <IconButton
                                          onClick={() =>
                                            setEditingLogo(!editingLogo)
                                          }
                                          size="small"
                                        >
                                          <Icon>edit</Icon>
                                        </IconButton>
                                      </Box>
                                    ))}
                                </Box>
                              )}
                            </Box>
                            <Box>
                              {entity.livestate !== C.ENTITY.LIVE_STATE.OFF && (
                                <Box
                                  p={2}
                                  mb={3}
                                  align="center"
                                  className={classes.meetingliveContainer}
                                >
                                  <Box pb={1}>
                                    <Typography variant="h6">
                                      {window.LANG.EXPO_LIVE}
                                    </Typography>
                                  </Box>
                                  <RedButton
                                    onClick={() => setViewLive(true)}
                                    size="large"
                                    startIcon={<Icon>videocam</Icon>}
                                  >
                                    {window.LANG.EXPO_LIVE_BTN}
                                  </RedButton>
                                </Box>
                              )}

                              {editing ? (
                                <Box p={1}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    label="Title"
                                    name="title"
                                    value={entity.title}
                                  />
                                </Box>
                              ) : (
                                entity.title && (
                                  <Typography variant="h5" gutterBottom>
                                    {entity.title}
                                  </Typography>
                                )
                              )}
                              {!editing &&
                                entity.entityList.events &&
                                entity.entityList.events
                                  .filter((event) => event.booking_settings)
                                  .map((event) => (
                                    <Box pt={1} pb={1} key={event.id}>
                                      <EntityBookingList
                                        entityId={entity.id}
                                        event={event}
                                        hasAdminAccess={entity.hasAdminAccess}
                                        bookingUpdates={bookingUpdates}
                                      />
                                    </Box>
                                  ))}
                              {editing ? (
                                <Box p={1}>
                                  <MarkdownEditor
                                    value={entity.description}
                                    setValue={(txt) =>
                                      setEntity({ ...entity, description: txt })
                                    }
                                  />
                                </Box>
                              ) : (
                                <Box>
                                  <Box
                                    ref={descriptionRef}
                                    className={classes.description}
                                    style={{
                                      maxHeight: showFullDescription
                                        ? 'none'
                                        : `${DESCRIPTION_MAX_HEIGHT}px`,
                                    }}
                                  >
                                    <MarkdownRender text={entity.description} />
                                  </Box>
                                  <Box align="center" className={classes.title}>
                                    {tooLongDescription && (
                                      <Button
                                        onClick={() =>
                                          toggleFullDescription(
                                            !showFullDescription
                                          )
                                        }
                                        startIcon={
                                          showFullDescription ? (
                                            <Icon>expand_less</Icon>
                                          ) : (
                                            <Icon>expand_more</Icon>
                                          )
                                        }
                                      >
                                        read{' '}
                                        {showFullDescription ? 'less' : 'more'}
                                      </Button>
                                    )}
                                  </Box>

                                  {!editing && entity.extra_data.ecards && (
                                    <Box pt={1} pb={1}>
                                      <Box
                                        p={1}
                                        className={classes.extraContainer}
                                      >
                                        <ReceiveECards
                                          data={entity.extra_data.ecards}
                                          entityId={entity.id}
                                          isHost={entity.isPublicProfile}
                                        />
                                      </Box>
                                    </Box>
                                  )}
                                  {!editing &&
                                    entity.extra_data.listVisitors && (
                                      <ListVisitors
                                        isHost={entity.hasAdminAccess}
                                        entityId={entity.id}
                                      />
                                    )}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                          {(listedPublicProfiles > 0 || editing) && (
                            <Grid
                              item
                              xs={12}
                              md={listedPublicProfiles > 3 ? 4 : 3} // sum up to 12
                              lg={listedPublicProfiles > 3 ? 4 : 3} // sum up to 10
                            >
                              <Box p={1}>
                                <Grid container spacing={1}>
                                  <PublicProfiles
                                    editing={editing}
                                    visitors={
                                      entity.hasAdminAccess
                                        ? entity.visitors
                                        : entity.visitors.filter(
                                            (v) =>
                                              v.entityVisitors.hidden === false
                                          ) || []
                                    }
                                    handleDelete={deleteProfile}
                                  />
                                </Grid>
                              </Box>
                              {!editing &&
                                entity.isPublicProfile &&
                                listedPublicProfiles > 1 && (
                                  <Box p={1} align="center">
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<Icon>forum</Icon>}
                                      onClick={() =>
                                        openNewThreadPopup(
                                          entity.visitors.filter(
                                            (v) => v.id !== authState.user.id
                                          ),
                                          'center'
                                        )
                                      }
                                    >
                                      Private host chat
                                    </Button>
                                    <br />
                                    Only between contact people in this room
                                    (for collaboration)
                                  </Box>
                                )}
                              {editing && (
                                <Box textAlign="center">
                                  <Button
                                    color="primary"
                                    onClick={() =>
                                      addHostRef.current.open({
                                        data: { entityId: props.entityId },
                                        callback: profileAdded,
                                      })
                                    }
                                    startIcon={<Icon>add_circle</Icon>}
                                  >
                                    add host
                                  </Button>
                                </Box>
                              )}
                            </Grid>
                          )}
                        </Grid>

                        {editing && (
                          <Box>
                            <Box align="right" p={1}>
                              <Button
                                startIcon={<Icon>arrow_right</Icon>}
                                color="secondary"
                                size="small"
                                onClick={() => setAdvanced(!editAdvanced)}
                              >
                                Advanced
                              </Button>
                            </Box>
                            {editAdvanced && (
                              <Grid
                                container
                                spacing={2}
                                className={classes.advancedSetting}
                              >
                                <Grid item xs={12} md={4}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        color="primary"
                                        checked={!!entity.extra_data.discussion}
                                        onChange={() =>
                                          toggleExtraData(
                                            'discussion',
                                            entity.extra_data.discussion
                                              ? null
                                              : {}
                                          )
                                        }
                                      />
                                    }
                                    label="Use Forum"
                                  />
                                  {entity.extra_data.discussion && (
                                    <Box pt={1}>
                                      <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={handleExtraDataChange(
                                          'discussion'
                                        )}
                                        label="Forum headline"
                                        name="headline"
                                        value={
                                          entity.extra_data.discussion
                                            .headline || ''
                                        }
                                      />
                                      <br />
                                      <br />
                                      <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={handleExtraDataChange(
                                          'discussion'
                                        )}
                                        label="Button title (during live)"
                                        name="togglebutton"
                                        placeholder="Forum"
                                        value={
                                          entity.extra_data.discussion
                                            .togglebutton || ''
                                        }
                                      />
                                      <br />
                                      <br />
                                      <ForumTopics
                                        entity={entity}
                                        handleExtraDataChange={
                                          handleExtraDataChange
                                        }
                                      />
                                    </Box>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        color="primary"
                                        checked={!!entity.extra_data.ecards}
                                        onChange={() =>
                                          toggleExtraData(
                                            'ecards',
                                            entity.extra_data.ecards ? null : {}
                                          )
                                        }
                                      />
                                    }
                                    label="Use Action Button"
                                  />
                                  <br />
                                  Can be used to receive E-Cards and sign up of
                                  things.
                                  {entity.extra_data.ecards && (
                                    <>
                                      <Box pt={1}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          onChange={handleExtraDataChange(
                                            'ecards'
                                          )}
                                          label="Button text"
                                          name="button"
                                          placeholder="Leave E-Card"
                                          value={
                                            entity.extra_data.ecards.button ||
                                            ''
                                          }
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      </Box>
                                      <Box pt={1}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          onChange={handleExtraDataChange(
                                            'ecards'
                                          )}
                                          label="Description"
                                          name="description"
                                          placeholder="Sign up for our news letter"
                                          value={
                                            entity.extra_data.ecards
                                              .description || ''
                                          }
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      </Box>

                                      <Box pt={1}>
                                        <TextField
                                          fullWidth
                                          variant="outlined"
                                          onChange={handleExtraDataChange(
                                            'ecards'
                                          )}
                                          label="Thanks message"
                                          name="thanks"
                                          placeholder="Thank you, we'll be in touch!"
                                          value={
                                            entity.extra_data.ecards.thanks ||
                                            ''
                                          }
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      </Box>
                                      <Box pt={1}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              color="primary"
                                              name="addmessage"
                                              checked={
                                                !!entity.extra_data.ecards
                                                  .addmessage
                                              }
                                              onChange={toggleExtraDataCheckbox(
                                                'ecards'
                                              )}
                                            />
                                          }
                                          label="Let people add message"
                                        />
                                      </Box>
                                    </>
                                  )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        color="primary"
                                        checked={!!entity.extra_data.pass}
                                        onChange={() =>
                                          toggleExtraData(
                                            'pass',
                                            entity.extra_data.pass ? null : {}
                                          )
                                        }
                                      />
                                    }
                                    label="Use Password"
                                  />
                                  {entity.extra_data.pass && (
                                    <Box pt={1}>
                                      <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={handleExtraDataChange('pass')}
                                        label="Headline (description)"
                                        name="headline"
                                        value={
                                          entity.extra_data.pass.headline || ''
                                        }
                                      />
                                      <br />
                                      <br />
                                      <TextField
                                        fullWidth
                                        variant="outlined"
                                        onChange={(e) =>
                                          handleExtraDataChange('pass')({
                                            target: {
                                              name: 'word',
                                              value: cipherEncode(
                                                e.target.value
                                              ),
                                            },
                                          })
                                        }
                                        label="Password"
                                        name="word"
                                        value={
                                          cipherDecode(
                                            entity.extra_data.pass.word
                                          ) || ''
                                        }
                                      />
                                      <br />
                                    </Box>
                                  )}
                                </Grid>
                                {authState.user.access_level ===
                                  C.VISITOR.ACCESS_LEVEL.MODERATOR && (
                                  <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={
                                            !!entity.extra_data.listVisitors
                                          }
                                          onChange={() =>
                                            toggleExtraData(
                                              'listVisitors',
                                              entity.extra_data.listVisitors
                                                ? null
                                                : {}
                                            )
                                          }
                                        />
                                      }
                                      label="List visitors"
                                    />
                                    <br />
                                    Only moderators can enable and only Hosts
                                    can view
                                  </Grid>
                                )}
                              </Grid>
                            )}
                          </Box>
                        )}

                        {!editing && entity.extra_data.discussion && (
                          <Grid
                            container
                            alignItems="flex-start"
                            justifyContent="center"
                          >
                            <Grid item xs={12} lg={10}>
                              <Box p={1} className={classes.extraContainer}>
                                <DiscussionList
                                  listType="connection"
                                  location={props.location}
                                  history={props.history}
                                  isEmbedded={true}
                                  connectionType="entity"
                                  connectionId={entity.id}
                                  hideMenu={true}
                                  title={
                                    entity.extra_data.discussion.headline ||
                                    'Forum'
                                  }
                                  topicSlug={
                                    entity.extra_data.discussion.slug || null
                                  }
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Container>
                    </Box>

                    {entity.mux_asset_id && (
                      <Box className={classes.videoHolder}>
                        <EntitySlide
                          data={{
                            type: 'video',
                            data: {
                              mux_asset_id: entity.mux_asset_id,
                              jwt: entity.jwt,
                              thumb_jwt: entity.thumb_jwt,
                            },
                          }}
                        />
                      </Box>
                    )}

                    {entity.hasAdminAccess && (
                      <EditSlide ref={editRef} entityId={props.entityId} />
                    )}
                    {entity.hasAdminAccess && editing && (
                      <Box
                        className={classes.saveButton}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box p={1}>
                          <Button
                            color="default"
                            size="small"
                            onClick={cancelEdit}
                          >
                            cancel
                          </Button>
                        </Box>
                        <Box p={1}>
                          <Button
                            color="primary"
                            size="small"
                            onClick={saveEdit}
                          >
                            save
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {editing && (
                      <Box pt={3} pb={1}>
                        <Divider />
                        <Box p={1} align="center">
                          Add images, text, embeds (like videos, forms, maps,
                          etc), comments or links.
                        </Box>
                      </Box>
                    )}
                    <Container maxWidth="lg">
                      {editing && (
                        <Box>
                          <AddSlide
                            open={true}
                            startEdit={startEdit}
                            index={0}
                          />
                        </Box>
                      )}
                      <DrawSlides
                        editing={editing}
                        slides={slideData.slides}
                        nrSlides={slideData.slides.length}
                        forceUpdate={slideData.update}
                        startEdit={startEdit}
                        deleteSlide={deleteSlide}
                        moveSlide={moveSlide}
                      />
                    </Container>
                  </Box>
                )}
              </>
            )}
          </Box>
        )
      )}
      <EntityAddVisitor
        ref={addHostRef}
        addMethod={addHost}
        useFieldForHidden={true}
      />
    </LiveContainer>
  );
};

export default EntityViewFullscreen;
